//#region IMPORTS
import {
  createInstoreTemplateAsync,
  fetchInstoreTemplateAsync,
  instoreTemplatesSelectors,
  updateInstoreTemplateAsync,
} from "../../features/instoreTemplates/instoreTemplatesSlice";
import {
  instoreTypesSelectors,
  fetchInstoreTypesAsync,
} from "../../features/instoreTypes/instoreTypesSlice";
import { parse, isValid as isValidDate, format } from "date-fns";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { InstoreTemplateRequest } from "../../models/instoreTemplate";
import { FieldValues, useForm } from "react-hook-form";
import { InstoreType } from "../../models/instoreType";
import { useEffect } from "react";
import { toast } from "react-toastify";
import SubmitButton from "../genericButtons/submitButton/SubmitButton";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  instoreTemplateId?: number;
  cancelCUD: () => void;
  disabled?: boolean;
  create?: boolean;
  isCopy?: boolean;
}
//#endregion

export default function InstoreTemplateForm({
  instoreTemplateId,
  cancelCUD,
  disabled = false,
  create = false,
  isCopy = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { instoreTypes_Loaded, loading } = useAppSelector(
    ({ instoreTypes }) => instoreTypes
  );
  const instoreTypes = useAppSelector(instoreTypesSelectors.selectAll);

  const instoreTemplate = useAppSelector((state) =>
    instoreTemplateId != null
      ? instoreTemplatesSelectors.selectById(state, instoreTemplateId)
      : undefined
  );

  // UseForm
  const {
    reset,
    setValue,
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({ mode: "all" });
  //#endregion

  //#region LOGIC
  // Fetch template data
  useEffect(() => {
    const fetchData = async (id: number) => {
      await dispatch(fetchInstoreTemplateAsync(id));
    };
    instoreTemplateId && !create && fetchData(instoreTemplateId);
  }, [instoreTemplateId, create, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchInstoreTypesAsync());
    };
    !instoreTypes_Loaded && fetchData();
  }, [instoreTypes_Loaded, dispatch]);

  useEffect(() => {
    if (instoreTemplate) {
      reset(instoreTemplate);

      setValue("order_type", String(instoreTemplate.order_type));

      if (instoreTemplate.from) {
        const fromDate = parse(
          instoreTemplate.from,
          "dd/MM/yyyy HH:mm",
          new Date()
        );
        if (isValidDate(fromDate)) {
          setValue("from", format(fromDate, "yyyy-MM-dd"));
        }
      }

      if (instoreTemplate.until) {
        const untilDate = parse(
          instoreTemplate.until,
          "dd/MM/yyyy HH:mm",
          new Date()
        );
        if (isValidDate(untilDate)) {
          setValue("until", format(untilDate, "yyyy-MM-dd"));
        }
      }
    }
  }, [instoreTemplate, setValue, reset]);

  // Submit form
  async function submitForm(data: FieldValues) {
    const selectedType = instoreTypes.find(
      (instoreType) => instoreType.name === data.instore_type
    );

    if (!selectedType) {
      toast.error("Selected instore type not found");
      return;
    }

    const request: InstoreTemplateRequest = {
      custom_template_id: data.custom_template_id,
      instore_type_id: selectedType.id,
      order_type: Number(data.order_type),
      active: data.active,
      from: data.from,
      until: data.until,
      template_rows: create
        ? [
            {
              theme_id: 1,
              theme: "Default",
              article_name: "My first article",
              store_format_id: selectedType.store_formats[0].id,
              store_format: selectedType.store_formats[0].name,
              amount: 2,
              index: 9999,
            },
          ]
        : data.template_rows,
    };

    try {
      if (instoreTemplate && !isCopy) {
        await dispatch(
          updateInstoreTemplateAsync({
            id: instoreTemplate.id,
            values: request,
          })
        );
      } else {
        await dispatch(
          createInstoreTemplateAsync({
            values: request,
          })
        );
      }
      cancelCUD();
    } catch (error) {
      toast.error(`${error}`);
    }
  }
  //#endregion

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="space-y-12">
        <div className="pb-12">
          {/* HEADER */}
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            {instoreTemplate
              ? `${isCopy ? "Copy template" : "Update template:"} ${
                  instoreTemplate.name
                }`
              : "Create new template"}
          </h2>

          {/* BODY */}
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* custom_template_id */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="custom_template_id"
                className={
                  errors.custom_template_id
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.custom_template_id
                  ? errors?.custom_template_id?.message
                  : "template id"}
              </label>
              <div className="relative mt-2">
                <input
                  placeholder="e.g. 9859"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading}
                  type="number"
                  id="custom_template_id"
                  {...register("custom_template_id", {
                    required: "Custom template id is required.",
                  })}
                />
              </div>
            </fieldset>

            {/* instore_type */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="instore_type"
                className={
                  errors.instore_type
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.instore_type
                  ? errors?.instore_type?.message
                  : "instore type"}
              </label>
              <div className="relative mt-2">
                <select
                  id="function"
                  defaultValue="Retail"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading}
                  {...register("instore_type", {
                    required: "This is a required field",
                  })}
                >
                  {instoreTypes_Loaded &&
                    instoreTypes?.map((type: InstoreType) => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                </select>
              </div>
            </fieldset>

            {/* order_type */}
            <fieldset className="sm:col-span-6">
              <label
                htmlFor="order_type"
                className={
                  errors.order_type
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.order_type ? errors?.order_type?.message : "order type"}
              </label>
              <div className="mt-3 space-y-6 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
                <div className="flex items-center">
                  <input
                    disabled={disabled || loading}
                    value={"1"}
                    type="radio"
                    className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 dark:border-white/10 dark:bg-white/5 dark:checked:border-indigo-500 dark:checked:bg-indigo-500 dark:focus-visible:outline-indigo-500 dark:disabled:border-white/5 dark:disabled:bg-white/10 dark:disabled:before:bg-white/20 forced-colors:appearance-auto forced-colors:before:hidden"
                    {...register("order_type")}
                  />
                  <label className="ml-3 block text-sm/6 font-medium text-gray-900 dark:text-white">
                    Opening
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    disabled={disabled || loading}
                    value={"2"}
                    type="radio"
                    className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 dark:border-white/10 dark:bg-white/5 dark:checked:border-indigo-500 dark:checked:bg-indigo-500 dark:focus-visible:outline-indigo-500 dark:disabled:border-white/5 dark:disabled:bg-white/10 dark:disabled:before:bg-white/20 forced-colors:appearance-auto forced-colors:before:hidden"
                    {...register("order_type")}
                  />
                  <label className="ml-3 block text-sm/6 font-medium text-gray-900 dark:text-white">
                    Relocation
                  </label>
                </div>
              </div>
            </fieldset>

            {/* from */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="from"
                className={
                  errors.from
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.from ? errors?.from?.message : "from date"}
              </label>
              <div className="relative mt-2">
                <input
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading}
                  type="date"
                  id="from"
                  {...register("from", {
                    required: "This is a required field",
                  })}
                />
              </div>
            </fieldset>

            {/* until */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="until"
                className={
                  errors.until
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.until ? errors?.until?.message : "until date"}
              </label>
              <div className="relative mt-2">
                <input
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading}
                  type="date"
                  {...register("until", {
                    required: "This is a required field",
                  })}
                />
              </div>
            </fieldset>

            {/* active */}
            <fieldset className="sm:col-span-6">
              <div className="relative flex gap-x-3">
                <div className="flex h-6 items-center">
                  <input
                    className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-600"
                    disabled={disabled || loading}
                    type="checkbox"
                    {...register("active")}
                  />
                </div>
                <div className="text-sm leading-6">
                  <label htmlFor="active" className="font-medium text-gray-900">
                    active
                  </label>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          onClick={cancelCUD}
          type="button"
          disabled={disabled || loading}
          className={classNames(
            disabled
              ? "text-gray-300 hover:cursor-not-allowed"
              : "text-rose-700",
            "text-sm font-semibold leading-6"
          )}
        >
          Cancel
        </button>

        <SubmitButton
          conditionals={[isValid]}
          isSubmitting={isSubmitting}
          disabled={disabled || loading}
        />
      </div>
    </form>
  );
}
